import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphCenter, HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import {
  HdCycleCardOrigin,
  HdCycleCardSlot,
  buildHdCycleChannelCardSources,
  buildHdCycleConditionedCenters,
  sortByHdCyclePriority,
  toHdCycleCardSlots,
} from '../hd-cycle-cards.builder';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdCollapsibleItems } from './hd-collapsible-items';
import { HdCycleCardsComponent } from './hd-cycle-cards.component';
import {
  HdCycleCenterBlockCause,
  HdCycleCenterBlockInfoRequest,
  HdCycleConditionedCenterBlockComponent,
} from './hd-cycle-conditioned-center-block.component';
import {
  HdChannelAnnotationPart,
  HdChannelListItemComponent,
  HdChannelListChannelInfoRequest,
  HdChannelListGateInfoRequest,
} from '../channel-list/hd-channel-list-item.component';
import { filterHdCycleOverlayActivations } from '../hd-cycle-overlay.utils';
import { HdCompositeChartInfoV1 } from '../hd-composite-chart.models';
import {
  HdCompositeChannelVm,
  HdCompositeKeynotesBreakdown,
  buildHdCompositeKeynotes,
} from '../hd-composite-keynotes.builder';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdRelationInfoTopic } from '../relation-info/hd-relation-info.service';
import { HD_JUST_NOW_TRANSIT_BASE_COLOR, HD_JUST_NOW_TRANSIT_TEXT_COLOR } from '../../mandala/just-now-transit/hd-just-now-transit';

type HdCompositeBlockKey = 'electromagnetics' | 'compromises' | 'dominances' | 'companionships';

/** Blok-accenten (kleur + FA-icoon); titels/brings komen uit de cycles-scope. */
const COMPOSITE_BLOCKS: readonly { key: HdCompositeBlockKey; color: string; faIcon: string }[] = [
  { key: 'electromagnetics', color: '#FF5959', faIcon: 'bolt' },
  { key: 'compromises', color: '#E0A030', faIcon: 'scale-unbalanced' },
  { key: 'dominances', color: '#1983BF', faIcon: 'crown' },
  { key: 'companionships', color: '#3BA55D', faIcon: 'handshake' },
];

/** Eén kanaal-item in een blok, met bij compromissen de richting-notitie. */
interface CompositeChannelEntryVm {
  /** Uitklap-key (blok + kanaal). */
  readonly key: string;
  readonly channel: HdCompositeChannelVm;
  /** "Steven ♂ > compromitteert > Anke's 57 ♀" als gekleurde delen; null bij andere varianten. */
  readonly note: readonly HdChannelAnnotationPart[] | null;
  /** I Tjing-kaarten van beide personen op de kanaal-poorten (chart 1 = chart-stijl, chart 2 = transit-stijl). */
  readonly cards: readonly HdCycleCardSlot[];
}

interface CompositeBlockVm {
  readonly key: HdCompositeBlockKey;
  readonly color: string;
  readonly faIcon: string;
  readonly title: string;
  readonly brings: string;
  readonly channels: readonly CompositeChannelEntryVm[];
}

/** Ongedefinieerd center van één persoon dat door de combinatie definitie krijgt. */
interface ConditionedCenterVm {
  readonly center: HdBodygraphCenter;
  readonly label: string;
  readonly text: string;
  /** Activaties van de andere persoon die het center definiëren (poort-pillen in diens kleur). */
  readonly causes: readonly HdCycleCenterBlockCause[];
}

interface PersonCentersVm {
  readonly name: string;
  readonly color: string;
  readonly centers: readonly ConditionedCenterVm[];
}

/**
 * "Relatie / Composiet details" in het Geavanceerd-paneel van een composite
 * chart: de vier relatie-blokken (Electromagnetic, Compromis, Dominantie,
 * Companionship — zonder keynote-groepen, encounter/renewal of omgevend, en zonder
 * view-toggle), gevolgd door de niet-zelf center thema's per persoon in twee
 * kolommen (chart 1 blauw, chart 2 groen), elk blokje klikbaar (center-info)
 * met de poort-pillen van de ander die het center definiëren. Kanaal-items
 * hergebruiken het cycle-blokjes-component; de poort-chips kleuren chart 1 als
 * natal (blauw) en chart 2 als cycle (groen). Elk kanaal-item heeft rechts een
 * uitklapknop voor de I Tjing-lijnkaarten van beide personen op het kanaal
 * (`hd-cycle-cards`, default dicht en lui gebouwd).
 */
@Component({
  selector: 'hd-composite-sections',
  templateUrl: './hd-composite-sections.component.html',
  styleUrl: './hd-composite-sections.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [HdChannelListItemComponent, HdCycleConditionedCenterBlockComponent, HdCycleCardsComponent],
})
export class HdCompositeSectionsComponent {
  /** Preset van de actieve tab; alleen een composite-preset rendert iets. */
  readonly preset = input<HdBodygraphChartPreset | null>(null);

  readonly gateInfoRequested = output<HdChannelListGateInfoRequest>();
  readonly channelInfoRequested = output<HdChannelListChannelInfoRequest>();
  /** Info-icoon op een blok-header of de niet-zelf-titel → relation-info-paneel (de page). */
  readonly relationInfoRequested = output<{ topic: HdRelationInfoTopic; origin: { x: number; y: number } }>();
  /** Klik op een niet-zelf center-blokje → center-info (de page). */
  readonly centerInfoRequested = output<HdCycleCenterBlockInfoRequest>();

  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly transloco = inject(TranslocoService);

  /** Uitklapstaat van de I Tjing-kaarten per kanaal-item: default dicht. */
  protected readonly items = new HdCollapsibleItems(false);

  constructor() {
    // Center-thema's komen uit de cycles-scope; beide talen eager voor de NL-fallback.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly visible = computed(() => this.preset()?.composite != null);

  private readonly breakdown = computed<HdCompositeKeynotesBreakdown | null>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) {
      return null;
    }
    return buildHdCompositeKeynotes(
      preset.planetaryActivations ?? [],
      filterHdCycleOverlayActivations(composite.partnerChart.planetaryActivations),
    );
  });

  // Titels en blok-teksten komen uit de cycles-scope (met NL-fallback) — het
  // bewezen content-taal-kanaal, zodat een EN-wissel ze meteen meevertaalt.
  protected readonly detailsTitle = computed(() => this.cyclesText('composite.detailsTitle', this.lang()));
  protected readonly centersTitle = computed(() => this.cyclesText('composite.centersTitle', this.lang()));

  /** Aria-label/tooltip van de relatieboek-info-iconen (zelfde key als het Eigenschappen-blok). */
  protected readonly relationInfoLabel = computed(() =>
    this.transloco.translate<string>('humanDesign.advancedPanel.composite.relationInfo', undefined, this.lang()));

  protected requestRelationInfo(topic: HdRelationInfoTopic, event: MouseEvent): void {
    this.relationInfoRequested.emit({
      topic,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  /** Alleen de niet-lege blokken, in vaste volgorde. */
  protected readonly blocks = computed<readonly CompositeBlockVm[]>(() => {
    const breakdown = this.breakdown();
    const composite = this.preset()?.composite;
    if (!breakdown || !composite) {
      return [];
    }
    const lang = this.lang();
    return COMPOSITE_BLOCKS
      .map(block => ({
        ...block,
        title: this.cyclesText(`composite.blocks.${block.key}.title`, lang),
        brings: this.cyclesText(`composite.blocks.${block.key}.brings`, lang),
        // Op planeetprioriteit van beide personen (zon eerst), zoals de
        // cycle-secties en het Just Now-paneel (Steven 2026-10-14).
        channels: sortByHdCyclePriority(
          breakdown[block.key].map(channel => ({
            key: `${block.key}:${channel.channelKey}`,
            channel,
            note: this.compromiseNote(channel, composite, lang),
            sources: buildHdCycleChannelCardSources(channel, { includeNatal: true }),
          })),
          entry => entry.sources,
        ).map(({ key, channel, note, sources }) => ({ key, channel, note, cards: toHdCycleCardSlots(sources) })),
      }))
      .filter(block => block.channels.length > 0);
  });

  /**
   * De compromis-richting op de poortenregel, als gekleurde delen mét de
   * planeetglyphs van de compromis-poort: wie het volledige kanaal draagt
   * compromitteert de (enige) poort van de ander — "Steven ♂ > compromitteert
   * > Anke's 57 ♀". Namen/glyphs kleuren mee met de chart (1 blauw, 2 groen).
   * Null voor andere varianten.
   */
  private compromiseNote(
    channel: HdCompositeChannelVm,
    composite: HdCompositeChartInfoV1,
    lang: string,
  ): readonly HdChannelAnnotationPart[] | null {
    if (channel.variant !== 'compromise' || channel.fullChannelChart == null) {
      return null;
    }
    const ownerFirst = channel.fullChannelChart === 1;
    const owner = ownerFirst ? composite.source.name : composite.partner.name;
    const other = ownerFirst ? composite.partner.name : composite.source.name;
    const ownerColor = ownerFirst ? HD_JUST_NOW_TRANSIT_BASE_COLOR : HD_JUST_NOW_TRANSIT_TEXT_COLOR;
    const otherColor = ownerFirst ? HD_JUST_NOW_TRANSIT_TEXT_COLOR : HD_JUST_NOW_TRANSIT_BASE_COLOR;
    // De gecompromitteerde poort = de kant waar de niet-drager zijn activatie heeft.
    const compromised = channel.gates.find(gate =>
      ownerFirst ? gate.cycleActivations.length > 0 : gate.natalActivations.length > 0);
    if (compromised == null) {
      return null;
    }
    const glyphsOf = (activations: readonly { glyph: string }[]) =>
      activations.map(activation => activation.glyph);
    const ownerGlyphs = glyphsOf(ownerFirst ? compromised.natalActivations : compromised.cycleActivations);
    const otherGlyphs = glyphsOf(ownerFirst ? compromised.cycleActivations : compromised.natalActivations);

    // Template "{{owner}} > … > {{other}}'s {{gate}}" opdelen rond de namen.
    const template = this.cyclesText('composite.compromiseNote', lang);
    const [beforeOwner, afterOwner = ''] = template.split('{{owner}}');
    const [between, tail = ''] = afterOwner.split('{{other}}');
    const otherText = `${other}${tail.replace('{{gate}}', `${compromised.gate}`)}`;

    const parts: HdChannelAnnotationPart[] = [];
    if (beforeOwner) parts.push({ text: beforeOwner });
    parts.push({ text: owner, color: ownerColor });
    if (ownerGlyphs.length > 0) parts.push({ glyphs: ownerGlyphs, color: ownerColor });
    if (between) parts.push({ text: between });
    parts.push({ text: otherText, color: otherColor });
    if (otherGlyphs.length > 0) parts.push({ glyphs: otherGlyphs, color: otherColor });
    return parts;
  }

  /**
   * Niet-zelf center thema's per persoon: ongedefinieerde centers van die
   * persoon die in de combinatie definitie krijgen (kolom 1 = chart 1, kolom 2
   * = chart 2), met als oorzaken de activaties van de andere persoon (pillen in
   * diens kleur). Teksten komen uit de cycles-scope (generieke niet-zelf-thema's).
   */
  protected readonly personCenters = computed<readonly PersonCentersVm[]>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) {
      return [];
    }
    const chart1Activations = filterHdCycleOverlayActivations(preset.planetaryActivations ?? []);
    const chart2Activations = filterHdCycleOverlayActivations(composite.partnerChart.planetaryActivations);
    const lang = this.lang();
    const conditionedFor = (
      ownConfiguration: HdBodygraphChartPreset['configuration'],
      otherActivations: readonly HdBodygraphPlanetaryActivation[],
      origin: HdCycleCardOrigin,
      causeColor: string,
    ): readonly ConditionedCenterVm[] =>
      buildHdCycleConditionedCenters(ownConfiguration, otherActivations, this.renderStateFactory, origin)
        .map(item => ({
          center: item.center,
          label: this.cyclesText(`centerConditioning.centers.${item.center}.label`, lang),
          text: this.cyclesText(`centerConditioning.centers.${item.center}.text`, lang),
          causes: item.causes.map(cause => ({
            glyph: cause.glyph,
            gate: cause.gate,
            line: cause.line,
            color: causeColor,
            title: `${this.planetInfo.planetInfoIn(cause.planetKey, lang)?.name ?? cause.planetKey} ${cause.gate}.${cause.line}`,
          })),
        }));
    return [
      {
        name: composite.source.name,
        color: HD_JUST_NOW_TRANSIT_BASE_COLOR,
        centers: conditionedFor(preset.configuration, chart2Activations, 'transit', HD_JUST_NOW_TRANSIT_TEXT_COLOR),
      },
      {
        name: composite.partner.name,
        color: HD_JUST_NOW_TRANSIT_TEXT_COLOR,
        centers: conditionedFor(composite.partnerChart.configuration, chart1Activations, 'chart', HD_JUST_NOW_TRANSIT_BASE_COLOR),
      },
    ];
  });

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }
}
